import { api } from '../../lib/api';

/**
 * Il contratto della pagina Oggi: `GET /admin/oggi`
 * (apps/api/src/modules/admin/admin.oggi.ts).
 *
 * Qui NON si fanno conti. Nemmeno una sottrazione: ogni numero che la pagina
 * mostra arriva gia' fatto dal server, perche' il browser ha in mano una
 * risposta paginata e non il database, e una somma fatta qui direbbe "questo
 * mese" intendendo "fra le righe che ho ricevuto".
 *
 * Le tre nature di un numero, che la pagina scrive sempre accanto alla cifra:
 *   FATTO       soldi gia' passati dal conto (incassati, pagati)
 *   CERTO       esiste un documento (fattura emessa, ricevuta, rata del fisco):
 *               l'importo e' quello, la data puo' slittare
 *   PREVISIONE  nessun documento: canoni proiettati, voci inserite a mano,
 *               valore delle trattative
 */

export type Natura = 'FATTO' | 'CERTO' | 'PREVISIONE';

export interface OggiPeriodo {
  from: string;
  to: string;
  label: string;
  entrate: {
    incassate: number;
    certe: number;
    previste: number;
    totale: number;
    scaduto: number;
    dettaglio: { fatture: number; ricorrenti: number; manuali: number };
  };
  uscite: {
    pagate: number;
    certe: number;
    previste: number;
    totale: number;
    scaduto: number;
    /** `tasseStima` sta DENTRO `tasse`: e' la parte senza un documento dietro, non si somma. */
    dettaglio: { fornitori: number; tasse: number; tasseStima: number; ricorrenti: number; manuali: number };
  };
  saldo: number;
  liquiditaAttesa: number;
}

export interface OggiRigaFattura {
  paymentId: string;
  invoiceId: string;
  numero: string | null;
  cliente: string;
  importo: number;
  dueDate: string | null;
  giorniDiRitardo: number;
  rata: { seq: number; su: number } | null;
}

export interface OggiRigaPagamento {
  fonte: 'FORNITORE' | 'TASSE' | 'CANONE' | 'MANUALE';
  id: string;
  controparte: string;
  descrizione: string | null;
  dueDate: string | null;
  importo: number;
  giorniDiRitardo: number;
  documento: boolean;
}

export interface OggiControllo {
  voci: Array<{ chiave: string; etichetta: string; valore: number; natura: Natura }>;
  totaleNetto: number;
  scartato: Array<{ chiave: string; etichetta: string; valore: number; count: number; regola: string }>;
  totaleLordo: number;
  differenza: number;
  quadra: boolean;
}

export interface OggiSummary {
  today: string;
  history: { cutoffDate: string | null; includeHistory: boolean };
  liquidita: { banca: number; contanti: number; totale: number; aggiornatoAl: string | null };
  questoMese: OggiPeriodo;
  prossimoMese: OggiPeriodo;
  intervallo: OggiPeriodo;
  lavoro: {
    inMano: {
      progetti: { valore: number; count: number; giaFatturato: number };
      trattativeVinte: { valore: number; count: number };
      /** Progetti aperti ma fatturati a rate: il valore sta gia' nei canoni, qui si mostra e non si somma. */
      progettiACanone: { valore: number; count: number };
      /** `aggregati` sta DENTRO `valore`: i canoni senza cliente, che la regola anti-doppione non può controllare. */
      ricorrente: { valore: number; count: number; mesi: number; fino: string; aggregati: { valore: number; count: number } };
      totale: number;
    };
    inVendita: {
      valore: number;
      pesato: number;
      count: number;
      rinnovi: { valore: number; count: number };
    };
  };
  fatture: {
    scadute: { count: number; totale: number; voci: OggiRigaFattura[] };
    prossime: { count: number; totale: number; voci: OggiRigaFattura[] };
  };
  daPagare: {
    /** `tasseStima` sta DENTRO `tasse`: la parte di Fisco ancora senza documento. */
    documenti: { fornitori: number; tasse: number; tasseStima: number; manuali: number; totale: number; scaduto: number };
    canoni: { totale: number; count: number; fino: string };
    totale: number;
    voci: OggiRigaPagamento[];
  };
  controllo: OggiControllo;
}

/**
 * `from` e `to` sono l'intervallo scelto a mano (punto 4 della lista di
 * Lorenzo). Entrano nella queryKey: cambiare periodo rifa' la chiamata e i
 * numeri si ricalcolano davvero, invece di essere tre bottoni che filtrano
 * qualcosa di gia' scaricato.
 */
export function oggiQuery(from?: string, to?: string) {
  const sp = new URLSearchParams();
  if (from) sp.set('from', from);
  if (to) sp.set('to', to);
  const qs = sp.toString();
  return {
    queryKey: ['admin', 'oggi', from ?? '', to ?? ''] as const,
    queryFn: () => api.get<OggiSummary>(`/admin/oggi${qs ? `?${qs}` : ''}`),
    // Stesso ritmo dell'overview: e' la stessa cassa, guardata da un'altra parte.
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  };
}

/** Etichetta breve da mettere accanto al numero. */
export const NATURA_LABEL: Record<Natura, string> = {
  FATTO: 'fatto',
  CERTO: 'certo',
  PREVISIONE: 'previsione',
};

export const FONTE_LABEL: Record<OggiRigaPagamento['fonte'], string> = {
  FORNITORE: 'Fornitore',
  TASSE: 'Fisco',
  CANONE: 'Canone',
  MANUALE: 'Previsione a mano',
};
